import {
  Box,
  Card,
  Divider,
  InlineGrid,
  Page,
  Text,
  TextField,
  BlockStack,
  useBreakpoints,
  Form,
  Button,
  ContextualSaveBar,
  Select,
  Checkbox,
  FormLayout,
} from '@shopify/polaris';
import { useField, useForm } from '@shopify/react-form';
import { useNotification } from 'context/NotificationContext';
import { useSettings } from 'context/SettingsContext';
import __ from 'languages/index';
import { useUpdateSetting } from 'queries/setting.query';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import __helpers from 'helpers/index';

export default function Embed() {
  const { addNotification } = useNotification();

  const { smUp } = useBreakpoints();

  const { settings: entities } = useSettings();
  const { mutateAsync: updateEntity, error, isSuccess: updateSuccess, isPending: updating } = useUpdateSetting();

  const useFields = {
    _matbao_taikhoan: useField<string>({ value: entities?._matbao_taikhoan ?? '', validates: [] }),
    _matbao_matkhau: useField<string>({ value: entities?._matbao_matkhau ?? '', validates: [] }),
    _matbao_api_uri: useField<string>({ value: entities?._matbao_api_uri ?? '', validates: [] }),
    _matbao_mst: useField<string>({ value: entities?._matbao_mst ?? '', validates: [] }),
    _matbao_khmshdon: useField<string>({ value: entities?._matbao_khmshdon ?? '', validates: [] }),
    _matbao_khhdon: useField<string>({ value: entities?._matbao_khhdon ?? '', validates: [] }),
    _matbao_thuesuat: useField<string>({ value: entities?._matbao_thuesuat ?? 5, validates: [] }),
    _matbao_auto_create_draft_invoice: useField<string>({ value: entities?._matbao_auto_create_draft_invoice ?? '0', validates: [] }),
    _matbao_auto_sign_invoice: useField<string>({ value: entities?._matbao_auto_sign_invoice ?? '0', validates: [] }),
    _google_maps_key: useField<string>({ value: entities?._google_maps_key ?? '', validates: [] }),
  };

  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForm,
    submitErrors,
    makeClean,
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateEntity({
          _matbao_taikhoan: values._matbao_taikhoan,
          _matbao_matkhau: values._matbao_matkhau,
          _matbao_api_uri: values._matbao_api_uri,
          _matbao_mst: values._matbao_mst,
          _matbao_khmshdon: values._matbao_khmshdon,
          _matbao_khhdon: values._matbao_khhdon,
          _matbao_thuesuat: values._matbao_thuesuat,
          _matbao_auto_create_draft_invoice: values._matbao_auto_create_draft_invoice,
          _matbao_auto_sign_invoice: values._matbao_auto_sign_invoice,
          _google_maps_key: values._google_maps_key,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success' };
    },
  });

  /**
   * Notification
   */

  useEffect(() => {
    if (error) {
      addNotification('error', error.message);
    }
  }, [error]);

  useEffect(() => {
    if (updateSuccess) addNotification('info', __('updated_successfully'));
  }, [updateSuccess]);

  return (
    <>
      <Helmet>
        <title>Cài đặt mã nhúng và kết nối</title>
      </Helmet>
      <Page title="Mã nhúng và kết nối">
        <BlockStack gap={{ xs: '800', sm: '400' }}>
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Mã nhúng cho web
                </Text>
                <Text as="p" variant="bodyMd">
                  Mã Passport.js là mã quan trọng, giúp các chức năng bên dưới hoạt động trơn tru và bảo vệ bạn khỏi Spambot tấn công.
                </Text>
              </BlockStack>
            </Box>
            <Card>
              <Text as="p" variant="bodySm" tone="subdued">
                Nhúng mã sau đây lên landing page của bạn, trước thẻ <code>&lt;/head&gt;</code> và để ở mọi trang.
              </Text>
              <TextField
                label=" "
                value={`<script crossorigin="anonymous" type="application/javascript" src="${process.env.REACT_APP_PUBLIC_URL}/passport.js"></script>`}
                onChange={() => {}}
                multiline={2}
                autoComplete="off"
              />
              <br />
              <Text as="p" variant="bodySm" tone="subdued">
                Nhúng mã sau đây lên landing page của bạn, trước thẻ <code>&lt;/body&gt;</code> và để ở trang bạn muốn tracking người dùng.
              </Text>
              <TextField
                label=" "
                value={`<script>
        var backend_url = '${process.env.REACT_APP_BACKEND_URL}/activity/web_activity'; 
        var requestee_option = {};
    </script>
    <script crossorigin="anonymous" defer="defer" type="application/javascript" src="${process.env.REACT_APP_PUBLIC_URL}/requestee.js"></script>`}
                onChange={() => {}}
                multiline={2}
                autoComplete="off"
              />
              <br />
              <Text as="p" variant="bodySm" tone="subdued">
                Nếu bạn muốn định nghĩa trang đích để tracking tốt hơn, bạn có thể đặt dữ liệu vào biến <br />
                <code>requestee_option.activity_target = 'YOUR_DEFINITION'; // định nghĩa tên trang đích </code> <br />
                <code>requestee_option.activity_referrer = 'YOUR_DEFINITION'; // định nghĩa trang giới thiệu </code> <br />
                Khi đó bạn có thể dễ dàng định nghĩa và lọc được người dùng truy cập một cách dễ dàng.
              </Text>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  Mắt bão Invoice API
                </Text>
                <Text as="p" variant="bodyMd">
                  Kết nối ứng dụng của bạn với dịch vụ hóa đơn của Mắt Bão
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">
              <Form onSubmit={submit}>
                <BlockStack gap={'200'}>
                  <Text as="h3" variant="headingSm">
                    Mắt bão Invoice API Connection
                  </Text>
                  <TextField label="Mã số thuế" autoComplete="off" {...fields._matbao_mst} />
                  <FormLayout.Group>
                    <TextField label="Tài khoản" autoComplete="off" {...fields._matbao_taikhoan} />
                    <TextField type="password" showCharacterCount label="Khóa bí mật/mật khẩu" autoComplete="off" {...fields._matbao_matkhau} />
                  </FormLayout.Group>

                  <FormLayout.Group condensed>
                    <TextField label="_matbao_khmshdon" autoComplete="off" {...fields._matbao_khmshdon} />
                    <TextField label="_matbao_khhdon" autoComplete="off" {...fields._matbao_khhdon} />
                    <TextField label="Thuế suất *** %?" autoComplete="off" {...fields._matbao_thuesuat} />
                  </FormLayout.Group>
                  <TextField label="API URI" autoComplete="off" {...fields._matbao_api_uri} />

                  <Checkbox
                    label="Tự động tạo nháp hóa đơn cho mọi đơn hàng sau 2 giờ."
                    checked={useFields._matbao_auto_create_draft_invoice.value === '1'}
                    onChange={() =>
                      useFields._matbao_auto_create_draft_invoice.onChange(useFields._matbao_auto_create_draft_invoice.value === '0' ? '1' : '0')
                    }
                    helpText="Khi bật tính năng này, hệ thống sẽ tự động tạo một bản nháp hóa đơn cho mỗi đơn hàng mới được tạo ra. Điều này giúp bạn tiết kiệm thời gian và công sức trong việc quản lý hóa đơn, đồng thời đảm bảo rằng mọi đơn hàng đều có một bản nháp hóa đơn sẵn sàng để xử lý khi cần thiết. Lưu ý là tính năng này sẽ chỉ tạo nháp hóa đơn và không tự động gửi chúng đi, bạn vẫn cần phải xem xét và xử lý các bản nháp này theo quy trình làm việc của mình."
                  />

                  <Checkbox
                    label="Tự động ký hóa đơn nháp sau 5 phút."
                    checked={useFields._matbao_auto_sign_invoice.value === '1'}
                    onChange={() => useFields._matbao_auto_sign_invoice.onChange(useFields._matbao_auto_sign_invoice.value === '0' ? '1' : '0')}
                    helpText="Với các hóa đơn nháp, hệ thống sẽ tự thay mặt bạn ký chúng sau 5 phút kể từ khi được tạo ra. Điều này giúp đảm bảo rằng các hóa đơn nháp được xử lý nhanh chóng và sẵn sàng để gửi đi khi cần thiết. Lưu ý rằng tính năng này sẽ tự động ký các hóa đơn nháp mà không cần sự can thiệp của bạn, vì vậy hãy đảm bảo rằng bạn đã xem xét và xác nhận nội dung của các hóa đơn nháp trước khi chúng được tự động ký."
                  />
                </BlockStack>
              </Form>
            </Card>
          </InlineGrid>

          {smUp ? <Divider /> : null}

          {/*
          <InlineGrid columns={{ xs: '1fr', md: '2fr 5fr' }} gap="400">
            <Box as="section" paddingInlineStart={{ xs: '400', sm: '0' }} paddingInlineEnd={{ xs: '400', sm: '0' }}>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">
                  ------
                </Text>
                <Text as="p" variant="bodyMd">
                  ----- ----- ----
                </Text>
              </BlockStack>
            </Box>
            <Card roundedAbove="sm">------</Card>
          </InlineGrid> */}

          {/** END */}
        </BlockStack>

        <br />
        <br />
        <br />
        <br />
      </Page>

      {dirty && (
        <ContextualSaveBar
          message="Thay đổi chưa được lưu"
          saveAction={{ onAction: () => submit(), loading: updating }}
          discardAction={{ onAction: () => resetForm() }}
        />
      )}
    </>
  );
}

